import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { Button, EmptyState, Field, Icon, Input, useDesignSystemTheme } from "@xinghai/ui";
import { ApiError, authApi } from "../../lib/api";
import { friendlyMessage } from "../../lib/apiErrors";
import { useTheme } from "../../app/theme";
import { confirmIssue, passwordHint, passwordIssues } from "./passwordRules";

function resetProblem(err: unknown): string {
  if (err instanceof ApiError && (err.code === "RESET_INVALID" || err.status === 404)) {
    return "这个重置链接无效，请确认是完整复制过来的。";
  }
  return friendlyMessage(err, "没能设置新密码，请重试。", {
    RESET_EXPIRED: "这个重置链接已经过期了（有效期 30 分钟）。请让老师再生成一个。",
    RESET_USED: "这个重置链接已经用过了。如果不是你用的，请立刻联系老师。",
    WEAK_PASSWORD: "新密码太弱了：至少 10 位，换一个别人猜不到的。",
  });
}

/** ACCOUNTS.md §4 — 一次性重置链接. */
export function ResetPasswordPage() {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  useDesignSystemTheme(theme);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);

  const reset = useMutation({
    mutationFn: () => authApi.resetPassword(token, password),
    onError: (err) => setError(resetProblem(err)),
  });

  if (reset.isSuccess) {
    return (
      <div className="xz-container">
        <EmptyState
          icon={<Icon id="ui-check-circle" size={24} />}
          title="新密码已经设好"
          description="用新密码登录就可以了。"
          action={
            <Link className="gz-button gz-button--primary" to="/login">
              去登录
            </Link>
          }
        />
      </div>
    );
  }

  const issues = password ? passwordIssues(password) : [];
  const mismatch = confirm ? confirmIssue(password, confirm) : null;
  const hint = passwordHint(password);

  return (
    <main className="xz-login">
      <form
        className="xz-login__card gz-panel gz-panel--raised"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          if (password.length === 0 || issues.length > 0 || mismatch) {
            setError("请先按提示修好密码。");
            return;
          }
          reset.mutate();
        }}
      >
        <h1 className="gz-type-heading">设置新密码</h1>
        <p className="gz-muted">设好之后用新密码登录。这个链接只能用一次。</p>

        <Field
          label="新密码"
          hint={password ? `${hint.label}：${hint.advice}` : "至少 10 位"}
          error={issues[0]}
        >
          <Input
            type="password"
            value={password}
            autoComplete="new-password"
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Field label="再输一次新密码" error={mismatch ?? undefined}>
          <Input
            type="password"
            value={confirm}
            autoComplete="new-password"
            onChange={(e) => setConfirm(e.target.value)}
          />
        </Field>

        {error && (
          <p className="gz-notice gz-notice--error" role="alert">
            <Icon id="ui-error" size={20} />
            {error}
          </p>
        )}

        <Button type="submit" variant="primary" size="lg" block loading={reset.isPending}>
          保存新密码
        </Button>
        <p className="gz-caption">
          <button
            type="button"
            className="gz-button gz-button--quiet"
            onClick={() => void navigate("/login")}
          >
            返回登录
          </button>
        </p>
      </form>
    </main>
  );
}
